import type { DashboardCounts } from '../dashboardTypes';

interface Segment { key: string; label: string; value: number; color: string }

/**
 * Payment Summary: a doughnut of the server's tab counts (they follow the form and date filters). Drawn as SVG with a text legend and a list, so it can be read
 * without colour or a canvas. The design groups Processing with Paid and splits Needs Attention; the server only counts by tab, so this shows the four tab groups.
 */
export function PaymentSummary({ counts, loading }: { counts: DashboardCounts | null; loading: boolean }) {
  const segments: Segment[] = counts ? [
    { key: 'paid', label: 'Paid', value: counts.paid, color: 'var(--tpd-green)' },
    { key: 'processing', label: 'Processing', value: counts.processing, color: 'var(--tpd-blue)' },
    { key: 'pending', label: 'Pending Payment', value: counts.pending, color: 'var(--tpd-amber)' },
    { key: 'attention', label: 'Needs Attention', value: counts.needsAttention, color: 'var(--tpd-red)' },
  ] : [];
  const total = counts?.all ?? 0;
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <section className="tpd-side-card" aria-labelledby="tpd-summary-title" data-testid="payment-summary">
      <h3 id="tpd-summary-title" className="tpd-side-card__title">Payment Summary</h3>
      <div className="tpd-donut">
        <svg viewBox="0 0 120 120" role="img" aria-label={loading ? 'Loading payment summary' : `${total} payments in total`}>
          <circle cx="60" cy="60" r={radius} fill="none" stroke="var(--tpd-line)" strokeWidth="16" />
          {total > 0 && segments.filter((s) => s.value > 0).map((s) => {
            const length = (s.value / total) * circumference;
            const circle = (
              <circle
                key={s.key} cx="60" cy="60" r={radius} fill="none" stroke={s.color} strokeWidth="16"
                strokeDasharray={`${length} ${circumference - length}`} strokeDashoffset={-offset} transform="rotate(-90 60 60)"
              />
            );
            offset += length;
            return circle;
          })}
          <text x="60" y="58" textAnchor="middle" className="tpd-donut__total">{loading ? '—' : total}</text>
          <text x="60" y="74" textAnchor="middle" className="tpd-donut__caption">Total</text>
        </svg>
      </div>
      <ul className="tpd-legend">
        {segments.map((s) => (
          <li key={s.key}>
            <span className="tpd-legend__dot" style={{ background: s.color }} aria-hidden="true" />
            <span className="tpd-legend__label">{s.label}</span>
            <span className="tpd-legend__value" data-testid={`summary-${s.key}`}>{s.value}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
